import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaHome, FaPlus, FaClipboardList, FaBell } from 'react-icons/fa';
import { IoChatbubbles } from 'react-icons/io5';
import { useChat } from './Chat/ChatContext';
import { useNotifications } from './Notifications/NotificationContext';
import CreateMenu from './CreateMenu';
import MyActivePostsDropdown from './MyActivePosts/MyActivePostsDropdown';
import NotificationDropdown from './Notifications/NotificationDropdown';
import './BottomNavBar.css';

function BottomNavBar() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { toggleChat, closeChat, isChatOpen, otherUnreadMessages } = useChat();
  const { unreadCount, isDropdownOpen, toggleDropdown, closeDropdown } = useNotifications();
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isCreateMenuOpen, setIsCreateMenuOpen] = useState(false);
  const [isMyPostsDropdownOpen, setIsMyPostsDropdownOpen] = useState(false);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          
          // Show when scrolling up, hide when scrolling down
          // But always show when at the top of the page
          if (currentScrollY < 10) {
            setIsVisible(true);
          } else if (currentScrollY < lastScrollY) {
            // Scrolling up
            setIsVisible(true);
          } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
            // Scrolling down (and not near top)
            setIsVisible(false);
          }
          
          setLastScrollY(currentScrollY);
          ticking = false;
        });
        
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollY]);

  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleCreateClick = () => {
    // Close other dropdowns before toggling
    if (!isCreateMenuOpen) {
      closeChat();
      closeDropdown();
      setIsMyPostsDropdownOpen(false);
    }
    setIsCreateMenuOpen(!isCreateMenuOpen);
  };

  const closeCreateMenu = () => {
    setIsCreateMenuOpen(false);
  };

  const handleMyPostsClick = () => {
    // Close other dropdowns before toggling
    if (!isMyPostsDropdownOpen) {
      closeChat();
      closeDropdown();
      setIsCreateMenuOpen(false);
    }
    setIsMyPostsDropdownOpen(!isMyPostsDropdownOpen);
  };

  const closeMyPostsDropdown = () => {
    setIsMyPostsDropdownOpen(false);
  };

  const handleNotificationClick = () => {
    // Close other dropdowns before toggling
    if (!isDropdownOpen) {
      closeChat();
      setIsCreateMenuOpen(false);
      setIsMyPostsDropdownOpen(false);
    }
    toggleDropdown();
  };

  const handleChatClick = () => {
    // Close other dropdowns before toggling
    if (!isChatOpen) {
      closeDropdown();
      setIsCreateMenuOpen(false);
      setIsMyPostsDropdownOpen(false);
    }
    toggleChat();
  };

  // Close notification dropdown when scrolling the page
  useEffect(() => {
    if (!isDropdownOpen) return;

    const handlePageScroll = () => {
      // Close dropdown when user scrolls the page
      toggleDropdown();
    };

    // Add scroll listener to window
    window.addEventListener('scroll', handlePageScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handlePageScroll);
    };
  }, [isDropdownOpen, toggleDropdown]);

  return (
    <nav className={`bottom-nav-bar ${isVisible ? 'visible' : 'hidden'}`}>
      <div className="bottom-nav-container">
        <button
          className="nav-item"
          onClick={() => handleNavigation('/home')}
          title={t('bottom_nav.home')}
        >
          <FaHome className="nav-icon" />
          <span className="nav-label">{t('bottom_nav.home')}</span>
        </button>

        <button
          className={`nav-item notification-nav-button NTS-notification-bell-container ${isDropdownOpen ? 'active' : ''}`}
          onClick={handleNotificationClick}
          title={t('bottom_nav.notifications')}
        >
          <div className="notification-icon-wrapper">
            <FaBell className="nav-icon" />
            {unreadCount > 0 && (
              <div className="nav-unread-badge">{unreadCount > 99 ? '99+' : unreadCount}</div>
            )}
          </div>
          <span className="nav-label">{t('bottom_nav.notifications')}</span>
        </button>

        <button
          className="nav-item center-button"
          onClick={handleCreateClick}
          title={t('bottom_nav.create')}
        >
          <div className="center-icon-wrapper">
            <FaPlus className="nav-icon" />
          </div>
          <span className="nav-label">{t('bottom_nav.create')}</span>
        </button>

        <button
          className={`nav-item ${isMyPostsDropdownOpen ? 'active' : ''}`}
          onClick={handleMyPostsClick}
          title={t('bottom_nav.my_posts')}
        >
          <FaClipboardList className="nav-icon" />
          <span className="nav-label">{t('bottom_nav.my_posts')}</span>
        </button>

        <button
          className="nav-item chat-nav-button"
          onClick={handleChatClick}
          title={t('bottom_nav.chat')}
        >
          <div className="chat-icon-wrapper">
            <IoChatbubbles className="nav-icon" />
            {otherUnreadMessages.size > 0 && (
              <div className="nav-unread-badge">{otherUnreadMessages.size}</div>
            )}
          </div>
          <span className="nav-label">{t('bottom_nav.chat')}</span>
        </button>
      </div>
      
      {/* Create Menu Dropdown */}
      <CreateMenu isOpen={isCreateMenuOpen} onClose={closeCreateMenu} />
      
      {/* My Active Posts Dropdown */}
      <MyActivePostsDropdown 
        isOpen={isMyPostsDropdownOpen} 
        onClose={closeMyPostsDropdown} 
      />
      
      {/* Notification Dropdown */}
      {isDropdownOpen && (
        <div className="bottom-nav-notification-dropdown-container NTS-notification-bell-container">
          <NotificationDropdown />
        </div>
      )}
    </nav>
  );
}

export default BottomNavBar;
